import { DEFAULT_LESSON_THEME, LESSON_THEMES } from '@teach/ui'
import type { BlockPatternId, LessonThemeId } from '@teach/ui'
import type { BlockCreateInput } from '@/types/blocks'
import type { ActiveBlockCard } from '../constants/blockTypeMatrix'

/** Оформление урока — то, что `readLessonLook` уже посчитал из темы урока
 *  и его блоков, для засева НОВОГО блока. */
export interface LessonLookSeed {
  theme: LessonThemeId | null
  pattern: BlockPatternId | null
}

/** bgColor/patternType для типа, что носит тему урока (info/design/article):
 *  без `look` — прежний засев якорем темы 1 (легаси-поведение), с `look` —
 *  тема и паттерн, посчитанные `readLessonLook` из секции оформления урока. */
const seed = (look?: LessonLookSeed) => ({
  bgColor: LESSON_THEMES.find((t) => t.id === look?.theme)?.anchor ?? DEFAULT_LESSON_THEME.anchor,
  patternType: look?.pattern ?? null,
})

// Builders intentionally omit `order` — the createBlock endpoint assigns it
// from the freshest cache state (see blocksApi).
//
// bgColor у типов, что носят тему (info/design/article), засеян якорем темы —
// тема выбирается ОДИН РАЗ на урок (секция оформления урока поверх
// useLessonLook/readLessonLook) и рассылается по этим блокам, но опознаётся
// по точному совпадению якорного хекса (см. resolveLessonThemeId) — новый
// блок с bgColor: null рисовал бы кремовый канвас, но секция оформления не
// подсвечивала бы ни одну тему как выбранную. У quiz/match/video своего
// фона нет вовсе — автор его не выбирает и не видит в студии (превью
// красится своим фиксированным blockTheme.quizCanvas), а плеер красит их из
// bgColor напрямую. Засеять якорь и туда значило бы молча перекрасить эти
// блоки у ученика в тему 1, не спросив автора. У этих типов bgColor остаётся
// null, как было.
//
// Типы, что носят тему (info/design, плюс article в articleBlockBody),
// принимают необязательный `look` — оформление урока (`readLessonLook`),
// которым засевается НОВЫЙ блок вместо якоря темы 1. quiz/match/video
// `look` не принимают вовсе — фона у них нет, и решение выше (bgColor: null)
// не меняется.
export function defaultBlockBody(card: ActiveBlockCard, look?: LessonLookSeed): BlockCreateInput {
  const base = {
    bgColor: null as string | null,
    bgColorEn: null,
    patternType: null as string | null,
    buttons: [],
  }
  switch (card.backendType) {
    case 'info':
      return {
        ...base,
        ...seed(look),
        type: 'info',
        image: null,
        imageEn: null,
        body: { bgColor: null },
        bodyEn: null,
      }
    case 'quiz':
      return {
        ...base,
        type: 'quiz',
        quizType: card.quizType ?? 'choice',
        multiAnswer: card.multiAnswer ?? false,
        correctAnswer: card.quizType === 'yes_no' ? true : null,
        correctFeedback: null,
        correctFeedbackEn: null,
        incorrectFeedback: null,
        incorrectFeedbackEn: null,
        image: null,
        imageEn: null,
        body: { question: null, answers: [] },
        bodyEn: { question: null, answers: [] },
      }
    case 'match':
      return { ...base, type: 'match', pairs: [] }
    case 'article':
      // Блок статьи создаётся пустым (PRD #71, ф.3) — статью автор выбирает потом.
      return articleBlockBody(null, look)
    case 'video':
      // п.19: видео-блок создаётся пустым, как и все остальные типы —
      // заливка ролика идёт на месте, в панели блока (VideoBlockEditor).
      return emptyVideoBlockBody()
    case 'design':
      return { ...base, ...seed(look), type: 'design', elements: [] }
  }
}

export function articleBlockBody(
  articleId: number | null,
  look?: LessonLookSeed,
): BlockCreateInput {
  return {
    type: 'article',
    articleId,
    ...seed(look),
    bgColorEn: null,
    buttons: [],
  }
}

// п.19: блок создаётся ПУСТЫМ — заливка ролика идёт на месте, в панели блока,
// а не в попапе перед созданием. Курс с таким блоком публиковать нельзя
// (см. `findEmptyVideoBlocks` + гейт в `PublishDialog`) — `oa-mobile` без
// ролика уходит опрашивать `videos/undefined` бесконечно.
export function emptyVideoBlockBody(): BlockCreateInput {
  return {
    type: 'video',
    videoId: null,
    videoIdEn: null,
    bgColor: null,
    bgColorEn: null,
    patternType: null,
    buttons: [],
  }
}
